import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts'

var SVC_COLORS = { stt: '#22D3EE', tts: '#FB7185', livekit: '#60A5FA', llm: '#34D399', other: '#94A3B8' }
var SVC_SHORT = { stt: 'STT', tts: 'TTS', livekit: 'LiveKit', llm: 'LLM', other: 'Other' }

var useStyles = makeStyles(function(theme) {
  return {
    section: { marginBottom: theme.spacing(3) },
    sectionLabel: {
      fontSize: 12, fontWeight: 700, color: '#444',
      textTransform: 'uppercase', letterSpacing: '0.07em',
      marginBottom: theme.spacing(1.5),
    },
    kpiCard: {
      padding: '18px 20px',
      border: '1px solid #e8ecf4',
      borderRadius: 12,
      borderLeft: '4px solid',
    },
    kpiLabel: { fontSize: 11, fontWeight: 700, color: '#444', textTransform: 'uppercase', letterSpacing: '0.05em' },
    kpiValue: { fontSize: 28, fontWeight: 800, fontFamily: 'monospace', marginTop: 4 },
    kpiDesc: { fontSize: 11, color: '#555', marginTop: 4 },
    funnelCard: { padding: '18px 20px', border: '1px solid #e8ecf4', borderRadius: 12 },
    funnelValue: { fontSize: 28, fontWeight: 800, fontFamily: 'monospace' },
    funnelPct: { fontSize: 13, fontWeight: 600, color: '#444', marginLeft: 8 },
    funnelDesc: { fontSize: 11, color: '#555', marginTop: 4 },
    chartCard: { padding: '16px 20px', border: '1px solid #e8ecf4', borderRadius: 12 },
    chartTitle: { fontSize: 14, fontWeight: 700, color: '#1a2540', marginBottom: 4 },
    chartSubtitle: { fontSize: 12, color: '#555', marginBottom: 16 },
    sessionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 12px',
      borderRadius: 8,
      border: '1px solid #f0f0f0',
      cursor: 'pointer',
      marginBottom: 6,
      '&:hover': { background: '#f8faff' },
    },
    sessionRank: {
      width: 22, height: 22, borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700, background: '#f5f5f5', flexShrink: 0,
    },
    sessionId: { fontSize: 12, fontWeight: 600, color: '#1a2540', fontFamily: 'monospace' },
    sessionMeta: { fontSize: 11, color: '#444', marginTop: 1 },
    svcPill: { fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4 },
    sessionCost: { fontSize: 13, fontWeight: 700, textAlign: 'right', flexShrink: 0 },
    emptyText: { textAlign: 'center', color: '#777', padding: '32px 0', fontSize: 13 },
  }
})

function fmtCost(v) {
  if (v == null) return '—'
  if (v < 0.0001) return '$0.0000'
  return '$' + v.toFixed(4)
}

function fmtDur(min) {
  if (!min) return '0s'
  if (min < 1) return Math.round(min * 60) + 's'
  return min.toFixed(1) + ' min'
}

function shortId(id) {
  if (!id) return '—'
  if (id.length <= 10) return id
  return id.slice(0, 6) + '…' + id.slice(-4)
}

function costColor(cost) {
  if (cost >= 0.15) return '#FB7185'
  if (cost >= 0.05) return '#FCD34D'
  return '#34D399'
}

function KPICard(props) {
  var classes = useStyles()
  return (
    <Paper className={classes.kpiCard} elevation={0} style={{ borderLeftColor: props.color }}>
      <Typography className={classes.kpiLabel}>{props.label}</Typography>
      <Typography className={classes.kpiValue} style={{ color: props.color }}>{props.value}</Typography>
      <Typography className={classes.kpiDesc}>{props.desc}</Typography>
    </Paper>
  )
}

export default function OverviewTab(props) {
  var classes = useStyles()
  var data = props.data
  var loading = props.loading

  var kpis = (data && data.kpis) || {}
  var dailySeries = (data && data.dailySeries) || []
  var topSessions = (data && data.topSessions) || []

  var convRate = (!loading && kpis.totalSessions > 0)
    ? Math.round((kpis.checkoutSessions / kpis.totalSessions) * 100) + '%'
    : (loading ? '…' : '—')

  var maxSessions = dailySeries.length > 0
    ? Math.max.apply(null, dailySeries.map(function(d) { return d.sessions || 0 }))
    : 0

  return (
    <div>

      {/* KPI row — business metrics only */}
      <div className={classes.section}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard
              label="Total Conversations"
              value={loading ? '…' : (kpis.totalSessions || 0)}
              color="#6366f1"
              desc="Voice conversations in range"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard
              label="Total Cost"
              value={loading ? '…' : fmtCost(kpis.totalCost)}
              color="#8b5cf6"
              desc="All services combined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard
              label="Avg Cost / Conversation"
              value={loading ? '…' : fmtCost(kpis.avgCost)}
              color="#14B7DD"
              desc="Per conversation"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPICard
              label="Conversion Rate"
              value={loading ? '…' : convRate}
              color="#10b981"
              desc="Conversations that completed a purchase"
            />
          </Grid>
        </Grid>
      </div>

      {/* Conversion funnel */}
      <div className={classes.section}>
        <Typography className={classes.sectionLabel}>Conversion Funnel</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Paper className={classes.funnelCard} elevation={0} style={{ borderLeft: '4px solid #818CF8' }}>
              <Typography style={{ fontSize: 11, fontWeight: 700, color: '#818CF8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Conversations
              </Typography>
              <Typography className={classes.funnelValue} style={{ color: '#818CF8' }}>
                {loading ? '…' : (kpis.totalSessions || 0)}
              </Typography>
              <Typography className={classes.funnelDesc}>Total voice conversations</Typography>
              <LinearProgress variant="determinate" value={100} style={{ marginTop: 10, height: 3, borderRadius: 999 }} />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper className={classes.funnelCard} elevation={0} style={{ borderLeft: '4px solid #34D399' }}>
              <Typography style={{ fontSize: 11, fontWeight: 700, color: '#34D399', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Added to Cart
              </Typography>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <Typography className={classes.funnelValue} style={{ color: '#34D399' }}>
                  {loading ? '…' : (kpis.cartSessions || 0)}
                </Typography>
                {!loading && kpis.totalSessions > 0 && (
                  <Typography className={classes.funnelPct}>
                    {Math.round((kpis.cartSessions / kpis.totalSessions) * 100)}%
                  </Typography>
                )}
              </div>
              <Typography className={classes.funnelDesc}>Conversations with cart activity</Typography>
              <LinearProgress
                variant="determinate"
                value={(!loading && kpis.totalSessions > 0) ? Math.round((kpis.cartSessions / kpis.totalSessions) * 100) : 0}
                style={{ marginTop: 10, height: 3, borderRadius: 999 }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper className={classes.funnelCard} elevation={0} style={{ borderLeft: '4px solid #FCD34D' }}>
              <Typography style={{ fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Checked Out
              </Typography>
              <div style={{ display: 'flex', alignItems: 'baseline' }}>
                <Typography className={classes.funnelValue} style={{ color: '#b45309' }}>
                  {loading ? '…' : (kpis.checkoutSessions || 0)}
                </Typography>
                {!loading && kpis.totalSessions > 0 && (
                  <Typography className={classes.funnelPct}>
                    {Math.round((kpis.checkoutSessions / kpis.totalSessions) * 100)}%
                  </Typography>
                )}
              </div>
              <Typography className={classes.funnelDesc}>Sessions that reached checkout</Typography>
              <LinearProgress
                variant="determinate"
                value={(!loading && kpis.totalSessions > 0) ? Math.round((kpis.checkoutSessions / kpis.totalSessions) * 100) : 0}
                style={{ marginTop: 10, height: 3, borderRadius: 999 }}
              />
            </Paper>
          </Grid>
        </Grid>
      </div>

      {/* Sessions trend + Top sessions */}
      <div className={classes.section}>
        <Grid container spacing={2}>

          {/* Conversations Over Time — daily volume */}
          <Grid item xs={12} md={8}>
            <Paper className={classes.chartCard} elevation={0}>
              <Typography className={classes.chartTitle}>Conversations Over Time</Typography>
              <Typography className={classes.chartSubtitle}>Daily conversation volume — see Analytics for cost breakdown</Typography>
              {dailySeries.length === 0 ? (
                <Typography className={classes.emptyText}>No data in range</Typography>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={dailySeries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="sessGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#6366f1" stopOpacity={0.85} />
                          <stop offset="100%" stopColor="#818CF8" stopOpacity={0.45} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#555' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#555' }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                      <Tooltip formatter={function(v) { return [v + ' conversation' + (v !== 1 ? 's' : ''), 'Volume'] }} />
                      <Bar dataKey="sessions" radius={[4, 4, 0, 0]}>
                        {dailySeries.map(function(entry, i) {
                          var isPeak = maxSessions > 0 && entry.sessions === maxSessions
                          return <Cell key={i} fill={isPeak ? '#6366f1' : 'url(#sessGrad)'} />
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: '#444' }}>
                      <span style={{ fontWeight: 700, color: '#6366f1', fontFamily: 'monospace' }}>
                        {dailySeries.reduce(function(s, d) { return s + (d.sessions || 0) }, 0)}
                      </span>{' '}total conversations
                    </div>
                    {maxSessions > 0 && (
                      <div style={{ fontSize: 11, color: '#444' }}>
                        peak <span style={{ fontWeight: 700, color: '#6366f1', fontFamily: 'monospace' }}>{maxSessions}</span> in a single day
                      </div>
                    )}
                  </div>
                </>
              )}
            </Paper>
          </Grid>

          {/* Top sessions by cost */}
          <Grid item xs={12} md={4}>
            <Paper className={classes.chartCard} elevation={0}>
              <Typography className={classes.chartTitle}>Top Conversations by Cost</Typography>
              <Typography className={classes.chartSubtitle}>Most expensive conversations — click to view</Typography>
              {topSessions.length === 0 ? (
                <Typography className={classes.emptyText}>No conversations in this range</Typography>
              ) : (
                topSessions.map(function(s, i) {
                  var maxCost = topSessions[0] ? topSessions[0].totalCost : 1
                  var pct = maxCost > 0 ? (s.totalCost / maxCost) * 100 : 0
                  var color = costColor(s.totalCost)
                  var activeSvcs = Object.entries(s.serviceCosts || {})
                    .filter(function(e) { return e[1] > 0 })
                    .map(function(e) { return e[0] })
                  return (
                    <div key={s.id} className={classes.sessionRow} onClick={function() { props.onSelectSession && props.onSelectSession(s) }}>
                      <div className={classes.sessionRank} style={i === 0 ? { background: 'rgba(252,211,77,0.15)', color: '#FCD34D' } : {}}>
                        {i + 1}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography className={classes.sessionId}>{shortId(s.id)}</Typography>
                        <Typography className={classes.sessionMeta}>
                          {fmtDur(s.durationMin)} · {s.turnCount} turn{s.turnCount !== 1 ? 's' : ''}
                        </Typography>
                      </div>
                      <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                        {activeSvcs.slice(0, 3).map(function(svc) {
                          return (
                            <span key={svc} className={classes.svcPill} style={{
                              background: (SVC_COLORS[svc] || '#94A3B8') + '18',
                              color: SVC_COLORS[svc] || '#94A3B8',
                              border: '1px solid ' + (SVC_COLORS[svc] || '#94A3B8') + '30',
                            }}>
                              {SVC_SHORT[svc] || svc.toUpperCase()}
                            </span>
                          )
                        })}
                      </div>
                      <div className={classes.sessionCost}>
                        <Typography style={{ fontSize: 13, fontWeight: 700, color: color }}>{fmtCost(s.totalCost)}</Typography>
                        <div style={{ marginTop: 3, height: 3, borderRadius: 999, background: '#f0f0f0', width: 64, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: pct + '%', background: 'linear-gradient(90deg, ' + color + '80, ' + color + ')', borderRadius: 999 }} />
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </Paper>
          </Grid>
        </Grid>
      </div>

    </div>
  )
}
